<script setup>
import { inject, ref, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, A11y } from 'swiper/modules'
import SlidePane from './SlidePane.vue'
import 'swiper/css'
import 'swiper/css/navigation'

const props = defineProps({
  selected: { type: Array, default: () => [] },
  data: {
    type: Object,
    default: () => {
      return {}
    }
  }
})

const emit = defineEmits(['update:selected', 'selectError', 'beforeSwiperChange'])

const loginRef = inject('loginRef')
const size = inject('size')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const voteStatus = inject('voteStatus')
const selectedNum = computed(() => props.selected.length)

// 分页 32個選項為一頁
const groupArray = (arr, size) => {
  const result = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}
const options = computed(() =>
  (props.data?.options || [])
    .filter((v) => v.enable)
    .map((v) => {
      v.selected = props.selected.includes(v.option_id)
      v.voted = props.data.user_voted_options.includes(v.option_id)
      return v
    })
)
const list = computed(() => groupArray(options.value, 32))

// 已选
// - 已投票选项不显示提示弹窗
const onSelect = (id) => {
  if (loginRef.value?.checklogin?.()) {
    if (props.data?.repeat_vote == 1 && props.data?.user_voted_options?.length !== 0) {
      let data = options.value.find((v) => v.option_id === id)
      if (!data?.voted) emit('selectError', id)
      return
    }
    let isCancel = false
    let res = props.selected
    if (res.includes(id)) {
      res = res.filter((v) => v !== id)
      isCancel = true
    } else {
      res = [...res, id]
    }
    if (res.length > props.data.max_option && !isCancel) return
    emit('update:selected', res)
  } else {
    loginRef.value?.login?.()
  }
}

const swiperRef = ref()
const realIndex = ref(-1)
const onAfterInit = (e) => {
  realIndex.value = e.realIndex
}
const onActiveIndexChange = (e) => {
  realIndex.value = e.realIndex
}
</script>

<template>
  <div class="tvb-vote-list" :class="'vote-list-' + size">
    <div v-if="voteStatus == 2" class="tvb-vote-list-tit">
      <span v-if="data.user_voted_options.length > 0" class="selected-tag">已投票</span>
      <span
        >請投選{{
          data.min_option === data.max_option
            ? `${data.min_option}個`
            : `${data.min_option}至${data.max_option}個`
        }}心水選擇</span
      ><span v-if="selectedNum !== 0" class="selected-tip"
        ><tvb-svg-icon class="selected-icon" name="icon_poll_selected" />&nbsp;當前已選{{
          selectedNum
        }}個</span
      >
    </div>

    <swiper
      ref="swiperRef"
      autoHeight
      :navigation="{
        prevEl: '.tvb-v-list-swiper-prev',
        nextEl: '.tvb-v-list-swiper-next'
      }"
      :spaceBetween="8"
      :breakpoints="{
        768: {
          spaceBetween: 16
        }
      }"
      :allowTouchMove="false"
      :modules="[Navigation, A11y]"
      class="tvb-vote-list-swiper"
      @afterInit="onAfterInit"
      @activeIndexChange="onActiveIndexChange"
      @beforeTransitionStart="emit('beforeSwiperChange')"
    >
      <swiper-slide v-for="(item, index) in list" :key="index">
        <slide-pane
          :data="data"
          :list="item"
          :only-show="voteStatus != 2"
          @select="onSelect"
          @rowHeightChange="swiperRef.$el.swiper.updateAutoHeight()"
        ></slide-pane>
      </swiper-slide>

      <template v-slot:container-end>
        <div v-if="list.length > 1" class="tvb-v-list-swiper-navigation">
          <div class="tvb-v-list-swiper-navigation-wrapper">
            <div class="tvb-v-list-swiper-prev" :class="{ 'nav-prev-hover': !isMobile }">
              <tvb-svg-icon class="prev-icon" name="icon_arrow_poll" />
            </div>
            <div class="tvb-v-list-swiper-pagination">
              <span
                v-for="(item, index) in list"
                :key="index"
                class="tvb-v-list-swiper-dot"
                :class="[
                  { 'page-dot-active': realIndex === index },
                  { 'page-dot-hover': !isMobile }
                ]"
                @click="swiperRef.$el.swiper.slideToLoop(index)"
              ></span>
            </div>
            <div class="tvb-v-list-swiper-next" :class="{ 'nav-next-hover': !isMobile }">
              <tvb-svg-icon class="next-icon" name="icon_arrow_poll" />
            </div>
          </div>
        </div>
        <div v-else-if="!isMobile" style="height: 16px"></div>
      </template>
    </swiper>
  </div>
</template>

<style lang="scss" scoped>
.tvb-vote-list {
  .tvb-vote-list-tit {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 22px;
    letter-spacing: 0.4px;
    margin-bottom: 24px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    .selected-tag {
      background: #ffffff;
      border-radius: 4px;
      font-size: 13px;
      color: #333333;
      line-height: 18px;
      width: 55px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .selected-icon {
      font-size: 16px;
      vertical-align: -0.15em;
      overflow: hidden;
    }
    .selected-tip {
      display: flex;
      align-items: center;
    }
  }

  // swiper navigation
  .tvb-v-list-swiper-navigation {
    margin-top: 32px;
    display: flex;
    justify-content: center;
    --tvb-v-s-nav-br: 26px;

    .tvb-v-list-swiper-navigation-wrapper {
      background-color: var(--tvb-vote-bgc);
      border-radius: var(--tvb-v-s-nav-br);
      display: flex;
      box-sizing: border-box;
      padding: 4px;
    }

    .tvb-v-list-swiper-prev,
    .tvb-v-list-swiper-next {
      width: 32px;
      height: 32px;
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: var(--tvb-v-s-nav-br);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      .prev-icon,
      .next-icon {
        font-size: 16px;
        vertical-align: -0.15em;
        overflow: hidden;
        opacity: 0.8;
        transition: opacity 0.15s linear;
      }
      &.swiper-button-disabled {
        cursor: not-allowed;
        .prev-icon,
        .next-icon {
          opacity: 0.35;
        }
      }
      &:not(.swiper-button-disabled).nav-prev-hover:hover,
      &:not(.swiper-button-disabled).nav-next-hover:hover {
        .prev-icon,
        .next-icon {
          opacity: 1;
        }
      }
    }
    .tvb-v-list-swiper-next .next-icon {
      transform: scaleX(-1);
    }

    .tvb-v-list-swiper-pagination {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0 16px;
      .tvb-v-list-swiper-dot {
        width: 10px;
        height: 10px;
        background-color: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        cursor: pointer;
        transition: background-color 0.15s linear;
        &.page-dot-active {
          background-color: rgba(255, 255, 255, 0.7);
        }
        &:not(.page-dot-active).page-dot-hover:hover {
          background-color: rgba(255, 255, 255, 0.7);
        }
      }
    }
  }

  .tvb-vote-list-swiper {
    .swiper-slide {
      min-height: 462px - 70px - 32px - 16px;
    }
  }

  &.vote-list-s,
  &.vote-list-xs {
    .tvb-vote-list-tit {
      font-size: 13px;
      line-height: 18px;
      letter-spacing: 0;
      align-items: stretch;
      margin-bottom: 16px;
      .selected-icon {
        font-size: 14px;
      }
      .selected-tag {
        border-radius: 3px;
        font-size: 12px;
        line-height: 18px;
        width: 48px;
        height: 18px;
      }
    }

    // swiper navigation
    .tvb-v-list-swiper-navigation {
      margin-top: 20px;
    }

    .tvb-vote-list-swiper {
      .swiper-slide {
        min-height: auto;
      }
    }
  }
}
</style>
